import { decodeHTML } from 'entities';
import pickBy from 'lodash/pickBy';
import { translateAccents } from '@/utils/translateCharacterAccents';
import xss from 'xss';

// use default xss filters and decode html symbols into a text string
// @ts-ignore
const sanitizeHtml = (text: string) => decodeHTML(xss(text));

export const getCategoryIdsFromPathname = (pathname: string = '') => {
    if (pathname === '/catalog/search') {
        return '0';
    }
    const categoryIds = pathname
        .split('/')
        .filter((x) => Number(x))
        .join(',');
    return categoryIds;
};

// gets the last categoryid from the path
export const getFinalCategoryIdFromPathname = (pathname: string = '') => {
    if (pathname === '/catalog/search') {
        return '';
    }
    const categoryIds = pathname.split('/').filter((x) => Number(x));
    if (categoryIds.length > 1) {
        return '';
    }
    return categoryIds[0];
};

// retrieves the last string from a path, which in some scenarios will be the lowest-level category desired
// returns category name formatted to be passed for wordpress api search
export const getCategoryNameFromPathname = (pathname: string = '') => {
    const categoryName = pathname.split('/').filter((x) => x && !Number(x));
    const cleanedUpCategoryName = categoryName.length ? categoryName[categoryName.length - 1].replace(/-/g, ' ') : '';
    return cleanedUpCategoryName;
};

export const getRawCategoryNameFromPathname = (pathname: string = '') => {
    const categoryName = pathname.split('/').filter((x) => x && !Number(x));
    return categoryName[categoryName.length - 1];
};

export const getIdFromPath = (path: string) => {
    if (!path) {
        return 0;
    }

    const pathString = `${path}`;
    const cleaned = pathString.replace(
        /^\/?(catalog(_gallery)?|item|auctioneer|catinfot|console|checkout|terms)\/?/,
        ''
    );
    let urlFrags = cleaned.split('_')[0];

    if (urlFrags.includes('/page')) {
        urlFrags = urlFrags.split('/')[0];
    }

    const idMatches = urlFrags.match(/^(\d+)\/?$/);
    return idMatches ? Number(idMatches[1]) || 0 : 0;
};

// https://stackoverflow.com/a/1714899/148256
// Returns a string from the object
export const toQueryString = (obj: any) => {
    const arr = [];

    // Checks if object has prototypes, if not, creates a new object with default prototypes
    if (Boolean(obj) && !Boolean(obj.__proto__)) {
        obj = { ...obj };
    }

    for (const p in obj) {
        if (obj.hasOwnProperty(p)) {
            arr.push(`${encodeURIComponent(p)}=${encodeURIComponent(obj[p])}`);
        }
    }
    arr.sort();
    return arr.join('&');
};

export const sellerIdFromPath = (path: string) => {
    if (!path) {
        return 0;
    }
    let sellerId = Number(path);

    if (!sellerId) {
        sellerId = Number(path.split('-')[0]);
    }
    return sellerId;
};

export const logoUrl = 'https://p1.liveauctioneers.com/dist/images/logo_og.jpg?auto=webp&format=pjpg';
export const signupLadySmallUrl =
    'https://p1.liveauctioneers.com/dist/images/SignupLadySmall.png?auto=webp&format=pjpg';
export const signupLadyPlaceholderUrl =
    'https://p1.liveauctioneers.com/dist/images/SignupLadySmall.png?auto=webp&format=pjpg&quality=15';
export const mobileLogo = 'https://p1.liveauctioneers.com/dist/images/logo-1024.png';
export const enableBrowserNotificationsUrl =
    'https://help.liveauctioneers.com/article/359-how-do-i-enable-browser-notifications';

export const urlSafeTitle = (title: string = '') => {
    let cleanedAndDecodedHtml = sanitizeHtml(title);
    cleanedAndDecodedHtml = translateAccents(cleanedAndDecodedHtml);
    const safe = cleanedAndDecodedHtml
        .trim()
        .toLowerCase() // & to and
        .replace(/&/g, 'and') // Replace , * ; : ' " to hyphen
        .replace(/[,*;:'"]+/g, '-') // Replace `- -` to single hyphen
        .replace(/-*\s+-*/g, '-') // Remove special characters
        .replace(/[^a-zA-Z0-9-_-]/g, '') // many hyphen to one
        .replace(/--+/g, '-') // Remove hyphen if it is the last character
        .replace(/-+$/, '');
    const urlEncoded = encodeURIComponent(safe).toLowerCase(); // lower case it again since the URI encoded values can be upper case
    return urlEncoded;
};

export const setQueryParams = (base: string, params: any = {}) => {
    let cleanedBase = base.replace(/\?.*/, '');
    const queryString = toQueryString(params);
    return queryString === '' ? cleanedBase : `${cleanedBase}?${queryString}`;
};

const buildSearchUrl = (base, params?: any) => {
    const queryString = toQueryString(params);
    return queryString === '' ? base : `${base}?${queryString}`;
};

export const getTodayUrl = () => '/trending/';

const getAuctionCalendarBaseUrl = () => '/catalog/search/';

export const getAuctionCalendarUrlWithParams = (params?: any) => {
    const base = getAuctionCalendarBaseUrl();
    return buildSearchUrl(base, params);
};

const getSearchBaseUrl = () => '/search/';
export const getUrlWithSearchParams = (params: any, base: string) => {
    return buildSearchUrl(base, params);
};
export const getSearchUrlWithParams = (params: any) => {
    const base = getSearchBaseUrl();
    return getUrlWithSearchParams(params, base);
};

export const getCategoryUrlWithParams = (base: string, params: any) => {
    return buildSearchUrl(base, params);
};

export const getCategoryFacetLink = (base: string, params: any) => {
    base = base === '' || base === undefined ? '/c/' : `/c/${base}/`;
    return buildSearchUrl(base, params);
};

//keywords: string could just be 'rings' or could be 'paintings/oil'
//categoryId: string. Could be '96' or could be '10,25830'(which is a subcategory)
export const getCategoryPageUrl = (keywords: string, categoryId: string, params?: any) => {
    const url = `/c/${keywords}/${categoryId.replace(',', '/')}/`;
    return buildSearchUrl(url, params);
};

export const getCombinedCategoryPageUrl = (firstCategory: any, secondCategory: any) => {
    const url = `/c/${urlSafeTitle(firstCategory.name)}/${urlSafeTitle(secondCategory.name)}/${firstCategory.id}/${
        secondCategory.id
    }/`;
    return buildSearchUrl(url);
};

const removeLastSlash = (text: string = '') => {
    if (text?.length && text[text.length - 1] === '/') {
        return text.substring(0, text.length - 1);
    }
    return text;
};

export const getACHTermsAndConditionsUrl = () => '/achtermsandconditions';
export const getAuctionResultsUrl = () => '/auction-results';
export const getAuctioneerDirectoryUrl = () => '/auctioneer/directory/';
export const getLiveAuctioneersCareersUrl = () => 'https://jobs.lever.co/liveauctioneers';
export const getCatalogUrl = (id: number, title: string) => `/catalog/${id}_${urlSafeTitle(title)}/`;
export const getCategoryLandingPageBaseUrl = () => '/c/';
export const getCatalogTermsUrl = (id: number, title: string) => `/terms/${id}_${urlSafeTitle(title)}`;
export const getCheckoutUrl = (catalogId: number) => `/checkout/${catalogId}`;
export const getConsignmentsUrl = () => '/consignments';
export const getCookiePolicyUrl = () => '/cookie-policy';
export const getDiscoverPageUrl = (topic: string) => removeLastSlash(`/discover/${topic}`);
export const getDisputeHelpUrl = () => 'https://help.liveauctioneers.com/article/163-how-to-handle-a-dispute';
export const getFeaturedCatalogsUrl = () => '/featured-auctions';
export const getFollowedSellersUrl = () => '/dashboard/followed-auctioneers';
export const getHomeUrl = () => '/';
export const getHowAuctionsWorkUrl = () => '/how-auctions-work';
export const getIntellectualPropertyUrl = () => '/intellectualproperty';
export const getItemUrl = (id: number, title: string) => `/item/${id}_${urlSafeTitle(title)}`;
export const isItemPage = (path: string = '') => path.includes('/item/');
export const getLiveBiddingUrl = (catalogId: number) => `/console/${catalogId}`;
export const getMessagesUrl = () => '/dashboard/messages';
export const getMyAuctionsUrl = () => '/dashboard/my-auctions';
export const getMyBidsUrl = () => '/dashboard/my-bids';
export const getNearMeUrl = () => '/find-auctions-near-me';
export const getNewsUrl = (base: string = 'https://www.liveauctioneers.com') => `${base}/news/`;
export const getNotFoundUrl = () => '/not-found';
export const getNotificationsUrl = (section?: string) =>
    `/dashboard/emails-and-notifications${section ? `?section=${section}` : ''}`;
export const getRecommendedItemsUrl = () => '/recommended';
export const getRecentItemsUrl = () => '/recent';
export const getReviewUrl = (catalogId: number) => `/review/${catalogId}`;
export const getPriceGuideBaseUrl = () => '/price-guide/';
export const getPriceGuideUrl = (name: string, id: number) => `${getPriceGuideBaseUrl()}${name}/${id}/`;
export const getPrivacyUrl = () => '/privacy';
export const getProfileUrl = (provider?: number) =>
    provider ? `/dashboard/profile?provider=${provider}` : '/dashboard/profile';
export const getSavedItemsUrl = () => '/dashboard/saved-items';
export const getSavedSearchesUrl = (append?: string) => `/dashboard/search-and-alerts/${append ? `${append}/` : ''}`;
export const getSellPageUrl = () => 'https://www.liveauctioneers.com/pages/sell/';
export const getSellerUrl = (id: number, title: string) => `/auctioneer/${id}/${urlSafeTitle(title)}/`;
export const getSellerReviewsUrl = (id: number, title: string, filter: string = '') =>
    filter
        ? `/auctioneer/${id}/${urlSafeTitle(title)}/reviews?filter=${filter}`
        : `/auctioneer/${id}/${urlSafeTitle(title)}/reviews`;
export const getStoreFrontUrl = (sellerId: number) => `/seller/${sellerId}`;
export const getSupportUrl = () => 'https://help.liveauctioneers.com';
export const getTerminologyUrl = () => '/terminology';
export const getTermsAndConditionsUrl = () => '/termsandconditions';
export const getWonItemsPageUrl = () => '/dashboard/won-items';
export const getTaxCertManagementUrl = () => '/dashboard/profile?tab=tax-cert-management';
export const getManageCategoryNotificationsUrl = (bidderEmail: string) =>
    `https://link.liveauctioneers.com/manage/4x3/category-management-v2?email=${bidderEmail}`;

export const addImageQueryParams = (url: string = '', width?: number, height?: number, quality?: number) => {
    if (!url) {
        return '';
    }
    const correctImageUrl = url.replace('http://', 'https://');
    const size = [];
    if (width) {
        size.push(`width=${width}&`);
    }
    if (height) {
        size.push(`height=${height}&`);
    }
    if (quality) {
        size.push(`quality=${quality}&`);
    }
    return `${correctImageUrl}?${size.join('')}auto=webp&format=pjpg`;
};

export const getAuctioneerLogoUrl = (auctioneer: string | null | undefined = '', width: number = 110) => {
    return `https://images.liveauctioneers.com/houses/logos/lg/${auctioneer}_large.jpg?auto=webp&format=pjpg&width=${width}&quality=80`;
};

export const getAuctioneerLogoUrlPlaceholder = (auctioneer: string | null | undefined = '', width: number = 110) => {
    return `${getAuctioneerLogoUrl(auctioneer, width)}&quality=15`;
};

export const getAllUpcomingAuctionsUrl = () => getAuctionCalendarUrlWithParams();

const toQString = (obj: any) => {
    const arr = [];

    // Checks if object has prototypes, if not, creates a new object with default prototypes
    if (Boolean(obj) && !Boolean(obj.__proto__)) {
        obj = { ...obj };
    }

    for (const p in obj) {
        if (obj.hasOwnProperty(p)) {
            arr.push(`${encodeURIComponent(p)}=${obj[p]}`);
        }
    }
    arr.sort();
    const joined = arr.join('&');
    return joined ? `?${joined}` : '';
};

type GetImageUrlParams = {
    blur?: any;
    catalogId?: number;
    crop?: any;
    height?: number;
    imageNumber?: number;
    imageVersion?: number;
    itemId?: number;
    quality?: any;
    sellerId?: number;
    width?: number;
};
export const getImageUrl = ({
    blur,
    catalogId,
    crop,
    height,
    imageNumber = 1,
    imageVersion = 1,
    itemId,
    quality,
    sellerId,
    width,
}: GetImageUrlParams) => {
    if (!sellerId || !catalogId || !itemId || !imageNumber) {
        return '';
    }

    const query = pickBy({
        auto: 'webp',
        blur,
        crop,
        format: 'pjpg',
        height,
        quality: typeof quality === 'undefined' ? 65 : quality,
        version: imageVersion,
        width,
    });

    return `https://p1.liveauctioneers.com/${sellerId}/${catalogId}/${itemId}_${imageNumber}_x.jpg${toQString(query)}`;
};

export const getThumbnailItemImageUrl = (
    sellerId: number,
    catalogId: number,
    itemId: number,
    imageNumber: number,
    imageVersion: number = 1,
    width: number = 128,
    quality: number = 80
) => getImageUrl({ catalogId, imageNumber, imageVersion, itemId, quality, sellerId, width });

export const getPlaceholderItemImageUrl = (
    sellerId: number,
    catalogId: number,
    itemId: number,
    imageNumber: number,
    imageVersion: number = 1
) => getImageUrl({ catalogId, imageNumber, imageVersion, itemId, quality: 10, sellerId, width: 612 });

export const getMediumItemImageUrl = (
    sellerId: number,
    catalogId: number,
    itemId: number,
    imageNumber: number,
    imageVersion: number = 1,
    quality: number = 80
) => getImageUrl({ catalogId, imageNumber, imageVersion, itemId, quality, sellerId, width: 512 });

export const getLargeItemImageUrl = (
    sellerId: number,
    catalogId: number,
    itemId: number,
    imageNumber: number,
    imageVersion: number = 1
) => getImageUrl({ catalogId, imageNumber, imageVersion, itemId, sellerId, width: 768 });

export const getXLargeItemImageUrl = (
    sellerId: number,
    catalogId: number,
    itemId: number,
    imageNumber: number,
    imageVersion: number = 1
) => getImageUrl({ catalogId, imageNumber, imageVersion, itemId, quality: 100, sellerId });

export const getCuratorImageUrl = (baseUrl: string, curatorId: number, curatorImageFileName: string) =>
    `${baseUrl}/${curatorId}/${encodeURIComponent(curatorImageFileName)}`;

export const getItemCardImageUrl = (
    sellerId: number,
    catalogId: number,
    itemId: number,
    imageNumber: number,
    imageVersion: number
) => getImageUrl({ catalogId, imageNumber, imageVersion, itemId, sellerId, width: 280 });

export const getMegaNavImageUrlLarge = (category: string) =>
    `https://p1.liveauctioneers.com/dist/images/desktop-${category}.jpg?auto=webp&format=pjpg&height=382`;

export const getMegaNavImageUrlSmall = (category: string) =>
    `https://p1.liveauctioneers.com/dist/images/tablet-${category}.jpg?auto=webp&format=pjpg&height=288`;

export const getMegaNavImageUrlLargePlaceholder = (category: string) =>
    `https://p1.liveauctioneers.com/dist/images/desktop-${category}.jpg?auto=webp&format=pjpg&height=382&quality=15`;

export const getMegaNavImageUrlSmallPlaceholder = (category: string) =>
    `https://p1.liveauctioneers.com/dist/images/tablet-${category}.jpg?auto=webp&format=pjpg&height=288&quality=15`;

export const getDeskSupportLink = () => 'https://help.liveauctioneers.com/';

export const getAdQuestionUrl = () => 'https://www.surveymonkey.com/r/KFCCVSX';

export const getSearchPageMostPopular = () => getSearchUrlWithParams({ page: 1, sort: '-popularity' });

const categoryLandingIds = {
    art: 1,
    asian: 158,
    collectibles: 2,
    fashion: 3,
    furniture: 4,
    'home-garden': 5,
    jewelry: 6,
};

export const getCategoryLandingPageUrlFromCategoryName = (category: string) => {
    const categoryId = categoryLandingIds[category];
    return categoryId
        ? `${getCategoryLandingPageBaseUrl()}${category}/${categoryId}/`
        : getCategoryLandingPageBaseUrl();
};

export const separateURL = (url: string) => {
    const match = url.match(/^(https?:)\/\/(([^:/?#]*)(?::([0-9]+))?)([/]{0,1}[^?#]*)(\?[^#]*|)(#.*|)$/);
    if (match === null) {
        return {
            host: '',
            path: '',
        };
    }
    return {
        host: match[2],
        path: match[5],
    };
};

export const getSearchAuctioneerUrl = (sellerId: number) =>
    getSearchUrlWithParams({
        house_id: `${sellerId}`,
        page: 1,
        sort: '-popularity',
    });

export const getSearchAuctioneerSoldUrl = (sellerId: number) =>
    getSearchUrlWithParams({
        house_id: `${sellerId}`,
        page: 1,
        status: 'archive',
    });

export const getUpcomingTitleSearchParams = (title: string = '', sellerId: number = 0) => {
    const keyword = title
        .split(/\s+/g)
        .map((word) => `${word}`)
        .join(' OR ')
        .replace(/"/g, '\\"');
    const params: any = { keyword, status: ['online', 'live', 'upcoming'] };
    if (sellerId) {
        params.house_id = sellerId.toString();
    }
    return params;
};

export const getSimilarItemsUrl = (title: string = '', sellerId: number = 0) => {
    const params = getUpcomingTitleSearchParams(title, sellerId);
    return getSearchUrlWithParams(params);
};

export const getCoverLotUrls = (
    catalogId: number,
    coverLots: any,
    sellerId: number,
    firstImageHeight: number,
    secondImageHeight: number,
    thirdImageHeight: number,
    catalogUrl?: string
) => {
    const { results } = coverLots || {};
    const [item1 = {}, item2 = {}, item3 = {}] = results;

    const coverLotImgAlt1 = item1.title;
    const coverLotImgAlt2 = item2.title;
    const coverLotImgAlt3 = item3.title;

    const coverLotImgSrc1 = getImageUrl({
        catalogId,
        height: firstImageHeight,
        imageNumber: item1.imageNumber || 1,
        imageVersion: item1.imageVersion,
        itemId: item1.itemId,
        sellerId,
    });

    const coverLotImgSrc1Placeholder = getImageUrl({
        catalogId,
        height: firstImageHeight,
        imageNumber: item1.imageNumber || 1,
        imageVersion: item1.imageVersion,
        itemId: item1.itemId,
        quality: 15,
        sellerId,
    });

    const coverLotImgSrc2 = getImageUrl({
        catalogId,
        height: secondImageHeight,
        imageNumber: item2.imageNumber || 1,
        imageVersion: item2.imageVersion,
        itemId: item2.itemId,
        sellerId,
    });

    const coverLotImgSrc2Placeholder = getImageUrl({
        catalogId,
        height: secondImageHeight,
        imageNumber: item2.imageNumber || 1,
        imageVersion: item2.imageVersion,
        itemId: item2.itemId,
        quality: 15,
        sellerId,
    });

    const coverLotImgSrc3 = getImageUrl({
        catalogId,
        height: thirdImageHeight,
        imageNumber: item3.imageNumber || 1,
        imageVersion: item3.imageVersion,
        itemId: item3.itemId,
        sellerId,
    });

    const coverLotImgSrc3Placeholder = getImageUrl({
        catalogId,
        height: thirdImageHeight,
        imageNumber: item3.imageNumber || 1,
        imageVersion: item3.imageVersion,
        itemId: item3.itemId,
        quality: 15,
        sellerId,
    });

    let coverLotImgHref1, coverLotImgHref2, coverLotImgHref3;
    if (catalogUrl) {
        coverLotImgHref1 = catalogUrl;
        coverLotImgHref2 = catalogUrl;
        coverLotImgHref3 = catalogUrl;
    } else {
        coverLotImgHref1 = getItemUrl(item1.itemId, item1.title);
        coverLotImgHref2 = getItemUrl(item2.itemId, item2.title);
        coverLotImgHref3 = getItemUrl(item3.itemId, item3.title);
    }

    return {
        primary: {
            imageAlt: coverLotImgAlt1,
            imageHref: coverLotImgHref1,
            imageSrc: coverLotImgSrc1,
            placeholderSrc: coverLotImgSrc1Placeholder,
        },
        secondary1: {
            imageAlt: coverLotImgAlt2,
            imageHref: coverLotImgHref2,
            imageSrc: coverLotImgSrc2,
            placeholderSrc: coverLotImgSrc2Placeholder,
        },
        secondary2: {
            imageAlt: coverLotImgAlt3,
            imageHref: coverLotImgHref3,
            imageSrc: coverLotImgSrc3,
            placeholderSrc: coverLotImgSrc3Placeholder,
        },
    };
};

const getWebviewsUrl = (path: string, deployment: string) => {
    let domain = `https://webviews${deployment === 'prod' ? '' : `-${deployment}`}.liveauctioneers.com`;
    return `${domain}/${path}`;
};

export const getPayrixAddCreditCardUrl = (deployment: string, address: any, customerId?: string) => {
    let url = getWebviewsUrl('addcreditcard/3', deployment);
    let queryParam = '';
    if (address) {
        queryParam = `address1=${encodeURIComponent(address.address1)}&address2=${encodeURIComponent(
            address.address2
        )}&state=${encodeURIComponent(address.state)}&countryCode=${encodeURIComponent(
            address.countryCode
        )}&city=${encodeURIComponent(address.city)}&postalCode=${encodeURIComponent(address.postalCode)}`;
    }
    if (customerId) {
        queryParam = `${queryParam}${queryParam ? '&' : ''}customerId=${encodeURIComponent(customerId)}`;
    }
    if (queryParam) {
        url = `${url}?${queryParam}`;
    }
    return url;
};

export const getPaysafeAddCreditCardUrl = (deployment: string, address: any, customerId?: string) => {
    let url = getWebviewsUrl('addcreditcard/4', deployment);
    let queryParam;
    if (address) {
        queryParam = `address1=${encodeURIComponent(address.address1)}&address2=${encodeURIComponent(
            address.address2
        )}&state=${encodeURIComponent(address.state)}&countryCode=${encodeURIComponent(
            address.countryCode
        )}&city=${encodeURIComponent(address.city)}&postalCode=${encodeURIComponent(address.postalCode)}`;
    }
    if (customerId) {
        queryParam = `${queryParam}${queryParam ? '&' : ''}customerId=${encodeURIComponent(customerId)}`;
    }
    if (queryParam) {
        url = `${url}?${queryParam}`;
    }
    return url;
};

export const getEkataTermsUrl = () => 'https://ekata.com/privacy/';

export const getStripeTermsUrl = () => 'https://stripe.com/privacy';

export const getPayrixTermsUrl = () => 'https://portal.payrix.com/terms';

export const getTaxCertCaptureUrl = (deployment: string, token: string, shipZone: string, userData: object) => {
    // exit with a falsy value if we are missing data.
    if (!token || !shipZone) {
        return '';
    }
    let url = getWebviewsUrl('taxcertcapture', deployment);
    let queryParam = '';
    if (token && shipZone) {
        queryParam = `token=${token}&shipZone=${shipZone}`;
    }
    if (userData) {
        queryParam = `${queryParam}&userData=${encodeURIComponent(JSON.stringify(userData))}`;
    }
    if (queryParam) {
        url = `${url}?${queryParam}`;
    }

    return url;
};

export const getCertCapturePageUrl = (shipZone: string) => {
    const url = '/certcapture';
    let queryParam = '';
    if (shipZone) {
        queryParam = `?shipZone=${shipZone}`;
    }
    return `${url}${queryParam}`;
};
